/**
 * Landing Hero canvas kit (FLYR-629).
 *
 * The four hero presets each animate something different over their photo, but
 * they all need the same lifecycle: size to the hero section box, draw at device
 * resolution, stay off below the lg breakpoint, paint one static frame under
 * prefers-reduced-motion instead of looping, and tear down cleanly on HMR. That
 * lifecycle lives in mountLandingHeroCanvas(); a preset supplies only its scene.
 *
 * The drawing helpers below cover what the trail-based presets share (a path
 * traced over time, coins sprinkled in its wake, a glowing head, the badge
 * check). A preset whose animation has a different shape can import the mount
 * alone and draw however it likes.
 *
 * Conventions follow the homepage hero canvas (src/js/heroCanvasV2.js), which is
 * the other canvas in this codebase.
 */

/**
 * A scene owns the drawing; the mount owns the clock and the canvas.
 *
 * createScene(ctx) returns:
 *   resize(width, height) — canvas box changed, rebuild anything sized to it
 *   step()                — advance one frame
 *   render()              — draw the current frame onto an already-cleared canvas
 *   finish()              — jump to the frame a still image should show, used
 *                           for prefers-reduced-motion
 */
export function mountLandingHeroCanvas(preset, createScene) {
    const canvas = document.querySelector(`[data-landing-hero-canvas="${preset}"]`);

    if (!canvas) {
        return;
    }

    const section = canvas.parentElement;
    const ctx = canvas.getContext('2d');

    // Matches the Tailwind lg breakpoint the canvas is revealed at.
    const desktop = window.matchMedia('(min-width: 1024px)');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    let width = 0;
    let height = 0;
    let rafId = null;

    // The mount clears, since it is what knows the box in CSS pixels; a scene
    // only ever draws.
    function paint() {
        ctx.clearRect(0, 0, width, height);
        scene.render();
    }

    function paintStatic() {
        scene.finish();
        paint();
    }

    // Handed to the scene so a late asset (the check icon) can repaint a still
    // frame that would otherwise never redraw. The animated loop needs nothing.
    function repaintIfStatic() {
        if (reduceMotion.matches) {
            paintStatic();
        }
    }

    const scene = createScene(ctx, { repaintIfStatic });

    function resize() {
        if (!desktop.matches) {
            return;
        }
        // Draw at device resolution so thin lines and their glow stay crisp on
        // retina displays. Assigning width/height resets the transform, so the
        // scale is set fresh rather than compounding across resizes.
        const dpr = window.devicePixelRatio || 1;
        width = section.clientWidth;
        height = section.clientHeight;
        canvas.width = width * dpr;
        canvas.height = height * dpr;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        scene.resize(width, height);
        if (reduceMotion.matches) {
            paintStatic();
        }
    }

    function loop() {
        scene.step();
        paint();
        rafId = requestAnimationFrame(loop);
    }

    function stop() {
        if (rafId !== null) {
            cancelAnimationFrame(rafId);
            rafId = null;
        }
    }

    function start() {
        stop();
        // Below lg the canvas is display:none, so never measure or paint it.
        if (!desktop.matches) {
            return;
        }
        resize();
        if (reduceMotion.matches) {
            paintStatic();
        } else {
            loop();
        }
    }

    desktop.addEventListener('change', start);
    reduceMotion.addEventListener('change', start);
    window.addEventListener('resize', resize);
    start();

    // Tear down on Vite HMR so reloads do not stack rAF loops or listeners.
    if (import.meta.hot) {
        import.meta.hot.dispose(() => {
            stop();
            window.removeEventListener('resize', resize);
            desktop.removeEventListener('change', start);
            reduceMotion.removeEventListener('change', start);
        });
    }
}

// Hub blue, the same value the homepage hero canvas draws its coins with.
export const COIN_RGB = '20, 123, 209';

// The comps' trail blue. No project token carries it. Presets that fade a stroke in
// need the channels rather than the hex.
export const TRAIL_RGB = '113, 204, 255';
export const TRAIL_COLOR = '#71ccff';

const CHECK_COLOR = '#2f7abe';

/**
 * The check the comps stamp into their badges (/media/svg/landing-hero-check.svg,
 * Amy's own asset, shipped alongside all three prototypes that draw one). It is
 * heavier than the icon-set check and squares off its ends rather than rounding
 * them, so the icon set is not a substitute. The file is already square and
 * already the comps' blue, so it loads as-is. Callers draw it at whatever size
 * they need: an SVG source rasterizes per drawImage call, so it stays sharp when
 * scaled.
 *
 * Returns an Image that may not have loaded yet. Presets draw a vector fallback
 * until it does, so there is no startup flash, and pass onReady to repaint a
 * static frame that would otherwise never redraw.
 */
export function loadCheckIcon(onReady) {
    const image = new Image();

    if (onReady) {
        image.onload = onReady;
    }
    image.src = '/media/svg/landing-hero-check.svg';

    return image;
}

/** Stroke a vector check, used until the icon image rasterizes. */
export function strokeCheckFallback(ctx, size) {
    const unit = size / 30;
    ctx.beginPath();
    ctx.moveTo(-10 * unit, 0);
    ctx.lineTo(-2.5 * unit, 7.5 * unit);
    ctx.lineTo(12.5 * unit, -10 * unit);
    ctx.strokeStyle = CHECK_COLOR;
    ctx.lineWidth = 5 * unit;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
}

/** Draw the check icon centered on the current origin, at the given size. */
export function drawCheck(ctx, icon, size) {
    if (icon.complete && icon.naturalWidth) {
        ctx.drawImage(icon, -size / 2, -size / 2, size, size);
    } else {
        strokeCheckFallback(ctx, size);
    }
}

/**
 * Turn one or more cubic bezier segments into a single t -> point function, with
 * t spread evenly across the segments. One segment is a plain curve; several
 * stitch end to end, which is how the comps draw their longer paths.
 */
export function bezierPath(segments) {
    const span = 1 / segments.length;

    return (t) => {
        const index = Math.min(Math.floor(t / span), segments.length - 1);
        const [p0, p1, p2, p3] = segments[index];
        const localT = (t - index * span) / span;
        const u = 1 - localT;
        const a = u * u * u;
        const b = 3 * u * u * localT;
        const c = 3 * u * localT * localT;
        const d = localT * localT * localT;
        return {
            x: a * p0.x + b * p1.x + c * p2.x + d * p3.x,
            y: a * p0.y + b * p1.y + c * p2.y + d * p3.y,
        };
    };
}

/**
 * Translucent coins scattered along the path, each anchored at its own point on
 * it and offset into the space around it. Rebuilt per cycle so no two laps
 * sprinkle the same way.
 */
export function sprinkleCoins(count) {
    return Array.from({ length: count }, (unused, i) => ({
        t: Math.min(i / count + Math.random() * 0.05, 1),
        offsetRadius: (Math.random() - 0.5) * 50,
        offsetAngle: Math.random() * Math.PI * 2,
        size: Math.random() * 8 + 3,
        alpha: Math.random() * 0.5 + 0.4,
    }));
}

// Coins and badges fade in over a short run of the trail once it passes them.
const FADE_RATE = 15;

export function fadeIn(progress, t) {
    return Math.min((progress - t) * FADE_RATE, 1);
}

/**
 * fadeFrom, when set, is the fraction of the path over which the trail itself fades
 * in. Coins inside that run are damped to match, so none of them pops against a
 * trail that is still translucent underneath.
 */
export function drawCoins(ctx, coins, pathAt, progress, fadeFrom = 0) {
    coins.forEach((coin) => {
        if (progress < coin.t) {
            return;
        }
        const center = pathAt(coin.t);
        const x = center.x + Math.cos(coin.offsetAngle) * coin.offsetRadius;
        const y = center.y + Math.sin(coin.offsetAngle) * coin.offsetRadius;
        const entry = fadeFrom && coin.t < fadeFrom ? coin.t / fadeFrom : 1;
        ctx.beginPath();
        ctx.arc(x, y, coin.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${COIN_RGB}, ${coin.alpha * fadeIn(progress, coin.t) * entry})`;
        ctx.fill();
    });
}

/**
 * A stroke for drawTrail that starts transparent and reaches full strength a short
 * way along the path, so the trail emerges from the photo instead of beginning at a
 * hard point.
 */
export function fadeInStroke(ctx, pathAt, fadeEnd) {
    const start = pathAt(0);
    const solid = pathAt(fadeEnd);
    const gradient = ctx.createLinearGradient(start.x, start.y, solid.x, solid.y);
    gradient.addColorStop(0, `rgba(${TRAIL_RGB}, 0)`);
    gradient.addColorStop(1, TRAIL_COLOR);
    return gradient;
}

/** The drawn-so-far portion of the path, glowing. */
export function drawTrail(ctx, pathAt, progress, stroke = TRAIL_COLOR) {
    const start = pathAt(0);
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    for (let t = 0; t <= progress; t += 0.01) {
        const point = pathAt(t);
        ctx.lineTo(point.x, point.y);
    }
    ctx.strokeStyle = stroke;
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.shadowColor = TRAIL_COLOR;
    ctx.shadowBlur = 10;
    ctx.stroke();
    ctx.shadowBlur = 0;
}

/** Glowing point riding the leading edge of the trail. */
export function drawHead(ctx, point, radius = 5) {
    ctx.beginPath();
    ctx.arc(point.x, point.y, radius, 0, Math.PI * 2);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
}
